import React, { useState } from 'react';
import { Project } from '../types/Project';

interface ProjectInputProps {
  onSubmit: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
}

export const ProjectInput: React.FC<ProjectInputProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    status: 'planning' as const,
    progress: 0
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーション
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) {
      newErrors.name = '案件名は必須です';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = '開始日は必須です';
    }
    
    if (!formData.endDate) {
      newErrors.endDate = '終了日は必須です';
    }
    
    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        newErrors.endDate = '終了日は開始日より後である必要があります';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit({
      ...formData,
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate)
    });
    
    // フォームをリセット
    setFormData({
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      status: 'planning',
      progress: 0
    });
    setErrors({});
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'progress' ? parseInt(value) || 0 : value
    }));
    
    // エラーをクリア
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'planning': return '計画中';
      case 'in-progress': return '進行中';
      case 'completed': return '完了';
      case 'on-hold': return '保留';
      default: return status;
    }
  };

  return (
    <div className="project-input-container">
      <div className="project-input-header">
        <h2>案件入力</h2>
        <p className="subtitle">新しい案件情報を入力してください</p>
      </div>

      <div className="project-input-content">
        <form onSubmit={handleSubmit} className="project-input-form">
          <div className="form-section">
            <h3 className="section-title">基本情報</h3>
            
            <div className="form-group">
              <label htmlFor="name" className="required">案件名</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
                placeholder="例: Webサイトリニューアルプロジェクト"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">説明</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="案件の詳細説明を入力してください"
              />
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">スケジュール</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate" className="required">開始日</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={errors.startDate ? 'error' : ''}
                />
                {errors.startDate && <span className="error-message">{errors.startDate}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="endDate" className="required">終了日</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className={errors.endDate ? 'error' : ''}
                />
                {errors.endDate && <span className="error-message">{errors.endDate}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">ステータス</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="status">ステータス</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="planning">計画中</option>
                  <option value="in-progress">進行中</option>
                  <option value="completed">完了</option>
                  <option value="on-hold">保留</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="progress">進捗率 (%)</label>
                <div className="progress-input-wrapper">
                  <input
                    type="number"
                    id="progress"
                    name="progress"
                    value={formData.progress}
                    onChange={handleChange}
                    min="0"
                    max="100"
                  />
                  <span className="progress-value">{formData.progress}%</span>
                </div>
                <input
                  type="range"
                  name="progress"
                  value={formData.progress}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className="progress-slider"
                />
              </div>
            </div>
          </div>

          <div className="form-preview">
            <h3 className="section-title">プレビュー</h3>
            <div className="preview-card">
              <div className="preview-row">
                <span className="preview-label">案件名:</span>
                <span className="preview-value">{formData.name || '(未入力)'}</span>
              </div>
              <div className="preview-row">
                <span className="preview-label">期間:</span>
                <span className="preview-value">
                  {formData.startDate && formData.endDate 
                    ? `${formData.startDate} 〜 ${formData.endDate}` 
                    : '(未入力)'}
                </span>
              </div>
              <div className="preview-row">
                <span className="preview-label">ステータス:</span>
                <span className="preview-value">{getStatusLabel(formData.status)}</span>
              </div>
              <div className="preview-row">
                <span className="preview-label">進捗率:</span>
                <span className="preview-value">{formData.progress}%</span>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              キャンセル
            </button>
            <button type="submit" className="btn-submit">
              作成
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

